import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { useInadimplenceByCities } from './api/HeatMapIntegration';

const HeatMap = ({year}: {
  year?: number | null;
}) => {
  const { data = [] } = useInadimplenceByCities(year); // Default to empty array
  const chartRef = useRef(null); // Ref for chart instance
  const chartDomRef = useRef(null); // Ref for DOM element

  useEffect(() => {
    // Skip if data is not available or empty
    if (!data || data.length === 0) {
      console.log('Data not available yet:', data);
      return;
    }

    const chartDom = chartDomRef.current;
    if (!chartDom) {
      console.error('Chart DOM element not found');
      return;
    }

    let myChart = chartRef.current;
    if (!myChart) {
      myChart = echarts.init(chartDom);
      chartRef.current = myChart;
    }

    //   exemplo:
    //   const data = [
    // { name: 'Maranguape', value: 9798732, coordinates: [-38.6819, -3.89065], inadimplencia: 5, quantidadeAlunos: 1500 },
    // { name: 'Maracanaú', value: 639236, coordinates: [-38.626, -3.87821], inadimplencia: 8, quantidadeAlunos: 800 },
    // { name: 'Fortaleza', value: 144205, coordinates: [-38.5434, -3.71839], inadimplencia: 10, quantidadeAlunos: 2000 },
    // { name: 'Palmácia', value: 18577, coordinates: [-38.8474, -4.15136], inadimplencia: 4, quantidadeAlunos: 200 },
    // { name: 'Pacatuba', value: 16632, coordinates: [-38.6161, -3.98436], inadimplencia: 6, quantidadeAlunos: 300 },
    // { name: 'Guaiúba', value: 7965, coordinates: [-38.6378, -4.04185], inadimplencia: 3, quantidadeAlunos: 100 },
    // ];

    // Calculate min and max for visualMap dynamically
    const values = data.map(city => city.value).filter(v => typeof v === 'number' && v >= 0);
    const minValue = Math.min(...values) || 0;
    const maxValue = Math.max(...values) || 10000000;

    fetch('/maps/brazil.json')
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to fetch map: ${response.statusText}`);
        }
        return response.json();
      })
      .then(brazilMap => {
        echarts.registerMap('brazil', brazilMap);

        const option = {
          tooltip: {
            trigger: 'item',
            formatter: (params) => {
              const { name, value } = params;
              if (!value) return '';

              const cityData = data.find(city => city.name === name);
              const formattedValue = new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              }).format(value);

              const inadimplencia = cityData ? cityData.inadimplencia : 'N/A';
              const quantidadeAlunos = cityData ? cityData.quantidadeAlunos : 'N/A';

              return `
                ${name}<br/>
                Valor: ${formattedValue}<br/>
                % de Inadimplência: ${inadimplencia}%<br/>
                Quantidade de Alunos: ${quantidadeAlunos}
              `;
            },
          },
          visualMap: {
            min: minValue,
            max: maxValue,
            left: 'left',
            bottom: '50px',
            text: ['High', 'Low'],
            calculable: true,
            inRange: {
              color: ['#FFBCBC', '#FF6666', '#FF3333', '#8E0606'],
            },
          },
          geo: {
            map: 'brazil',
            roam: true,
            center: [-38.5, -4.0],
            zoom: 7,
            label: {
              show: true,
              emphasis: {
                show: true,
              },
            },
            scaleLimit: {
              min: 1,
              max: 10,
            },
            itemStyle: {
              normal: {
                areaColor: '#D3D3D3',
                borderColor: '#A9A9A9',
              },
              emphasis: {
                areaColor: '#C0C0C0',
              },
            },
          },
          series: [
            {
              name: 'Volumes',
              type: 'map',
              map: 'brazil',
              geoIndex: 0,
              data: data.map(city => ({
                name: city.name,
                value: city.value,
              })),
              itemStyle: {
                areaColor: '#dddddd',
                borderColor: '#aaaaaa',
                borderWidth: 1,
              },
              emphasis: {
                itemStyle: {
                  areaColor: '#C0C0C0',
                },
              },
            },
          ],
        };

        myChart.setOption(option);
      })
      .catch(error => {
        console.error('Error loading map:', error);
      });

    // Cleanup
    return () => {
      if (myChart) {
        myChart.dispose();
        chartRef.current = null;
      }
    };
  }, [data]); // Add data as dependency

  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={chartDomRef}
        id="heatmap"
        style={{ width: '100%', height: '600px', background: '#f0f0f0' }} // Light background for visibility
      ></div>
      {data.length === 0 && <div>Loading data...</div>}
      <div
        style={{
          position: 'absolute',
          top: '0',
          marginTop: '-80px',
          right: '20px',
          background: 'white',
          padding: '10px',
          zIndex: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div
            style={{
              width: '15px',
              height: '15px',
              backgroundColor: 'red',
              borderRadius: '50%',
              marginRight: '10px',
            }}
          />
          <span style={{ color: '#ccc' }}>
            Quanto mais forte a cor, mais inadimplência é a área analisada
          </span>
        </div>
      </div>
    </div>
  );
};

export default HeatMap;